import React, { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import Button from "../components/common/button";
import Modal from "../components/common/modal";
import authService from "../services/authService";
import { SubscriptionService } from "../services/subscriptionService";
import { useStripe } from "@stripe/react-stripe-js";
import { Snackbar, Alert } from "@mui/material";

const Plans = () => {
  const [selectedPlan, setSelectedPlan] = useState("multi");
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState();
  const [multiLocationModalOpen, setMultiLocationModalOpen] = useState(false);
  const [multiLocationCount, setMultiLocationCount] = useState(2);
  const stripe = useStripe();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const planResp = await SubscriptionService.getPlans();
        setPlans(planResp || []);
      } catch (error) {
        console.error("Failed to fetch plans", error);
      }
    };

    const fetchUser = async () => {
      try {
        const resp = await authService.getProfile();
        setSubscription(resp);
      } catch (error) {
        console.error("Failed to fetch user", error);
      }
    };

    fetchPlans();
    fetchUser();
  }, []);

  // Find Stripe plan IDs
  const singleLocationPlan = plans.find((p) => p.unit_amount === 7999);
  const multiLocationPlan = plans.find((p) => p?.product?.name === "Multi Location");

  const calculateMultiLocationPrice = (locationCount) => {
    const base = multiLocationPlan ? multiLocationPlan.unit_amount / 100 : 129.99;
    if (locationCount <= 2) {
      return base; // Base price for 2 locations
    }
    const additionalLocations = locationCount - 2;
    return base + additionalLocations * 50;
  };

  const handleCheckout = async (planId, quantity = 1, amountDollars, type) => {
    if (!stripe) {
      setSnackbarMessage("Stripe is not loaded yet.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    try {
      const isFree = type === "free";
      const res = await SubscriptionService.createCheckoutSession(
        planId,
        quantity,
        amountDollars,
        isFree,
        'plans'
      );
      const data = res;
      if (data.id) {
        const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
        if (error) {
          setSnackbarMessage(error.message || "Stripe checkout failed.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } else {
          try {
            const sub = await authService.getProfile();
            setSubscription(sub);
            setSnackbarMessage("Subscription updated successfully.");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
          } catch (e) {
            setSnackbarMessage("Failed to update subscription after payment.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        }
      } else {
        setSnackbarMessage("Failed to create Stripe session.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage("Error creating Stripe session.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const currentPlan = subscription?.current_subscription?.plan;
  const locationQuantity = subscription?.current_subscription?.locations;

  return (
    <div className="min-h-screen  flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
        {/* Left Section - Information */}
        <div className="w-full md:w-[45%] bg-[#f8f9fa] p-6 md:p-8 flex flex-col">
          <div className="mb-6 rounded-2xl overflow-hidden shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=2070&auto=format&fit=crop"
              alt="Restaurant Staff"
              className="w-full h-40 object-cover"
            />
          </div>

          <h1 className="text-2xl text-start font-bold text-slate-900 leading-tight mb-3">
            Upgrade to unlock full menu mastery
          </h1>

          <p className=" text-start text-sm text-slate-600 mb-6 leading-relaxed">
            Turn your restaurant into the place you always dreamed it could be —
            where every staff member knows your food, wine, and guests inside
            and out.
          </p>

          <div className="space-y-4 flex-grow text-start">
            <div className="flex ">
              <span className="text-xl mb-4">🍄</span>
              <div>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900 italic">
                    Advanced Food Units
                  </span>{" "}
                  teach allergens, dietary restrictions, and guest
                  accommodations — protecting your guests and building long-term
                  loyalty.
                </p>
              </div>
            </div>

            <div className="flex ">
              <span className="text-xl mb-4">🍷</span>
              <div>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900 italic">
                    Advanced Wine Units
                  </span>
                  train your team to speak confidently about your entire wine
                  list, introduce wine styles, and sell every bottle — not just
                  the usual favorites.
                </p>
              </div>
            </div>

            <div className="flex ">
              <span className="text-xl mb-4">💬</span>
              <div>
                <p className="text-sm text-slate-600">
                  Your staff become the most professional, knowledgeable servers
                  your restaurant has ever had.
                </p>
              </div>
            </div>

            <div className="flex ">
              <span className="text-xl mb-4">⚙️</span>
              <div>
                <p className="text-sm text-slate-600">
                  Automated lessons for every menu item — food or wine — keep
                  training accurate and up-to-date.
                </p>
              </div>
            </div>

            <div className="flex ">
              <span className="text-xl mb-4">📊</span>
              <div>
                <p className="text-sm text-slate-600">
                  Manager dashboards track completion, engagement, and sales
                  impact in real time.
                </p>
              </div>
            </div>

            <div className="flex ">
              <span className="text-xl mb-4">🛡️</span>
              <div>
                <p className="text-sm text-slate-600 font-medium italic text-slate-800">
                  The complete Speak Your Menu system makes you the restaurant
                  you wanted to open — the one your customers remember and
                  return to.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white rounded-2xl border-l-4 border-[#881337] shadow-sm">
            <span className="text-6xl w-10 text-start text-[#881337] block font-serif">
              “
            </span>
            <p className="text-slate-700 text-start text-sm italic mb-3 leading-relaxed">
              "We upgraded and saw an immediate change — staff were confident,
              guests were impressed, and training ran itself."
            </p>
            <p className="text-slate-500 text-start font-medium text-xs">
              Restaurant Owner, NYC
            </p>
          </div>
        </div>

        {/* Right Section - Plan Selection */}
        <div className="w-full md:w-[55%] p-6 md:p-8 relative">
          <button className="absolute right-4 top-4 p-2 text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>

          <h2 className="text-xl font-bold text-slate-900 mb-6 text-start">
            Choose Your Plan
          </h2>

          <div className="space-y-4">
            {/* Single Location Plan */}
            <div
              onClick={() => setSelectedPlan("single")}
              className={`cursor-pointer border-2 rounded-2xl p-5 transition-all bg-white text-start ${selectedPlan === "single"
                  ? "border-[#881337] bg-rose-50/50 shadow-lg"
                  : "border-slate-200 hover:border-slate-300"
                }`}
            >
              <div className="flex justify-between items-start mb-1 text-start">
                <h3 className="text-md font-bold text-slate-900">
                  Single Location
                </h3>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-[#881337]">
                  ${singleLocationPlan ? (singleLocationPlan.unit_amount / 100).toFixed(2) : "79.99"}
                </span>
                <span className="text-slate-500 text-xs text-start">
                  / month
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-4 text-start">
                Perfect for individual restaurants ready to train their full
                team
              </p>

              <ul className="space-y-2 mb-2 text-sm">
                {[
                  "Full access to all Food & Wine training units",
                  "Advanced lessons on allergens, restrictions, and accommodations",
                  "Unlimited users — no per-employee fees",
                  "Manager dashboard with detailed progress analytics",
                  "Automatic lesson generation from your menu uploads",
                ].map((feature, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-slate-700">
                    <Check
                      size={18}
                      className="text-white bg-[#881337] rounded-full m-2   shrink-0 mt-0.5"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {currentPlan === "Single Location" ? (
                <Button className="w-full py-3 bg-gray-300 text-gray-700 rounded-xl text-sm" disabled>
                  Already subscribed
                </Button>
              ) : (
                <Button
                  className="w-full py-3 bg-[#881337] text-white  rounded-xl hover:bg-[#6e0f2c] transition-colors shadow-lg shadow-red-900/20 text-sm"
                  onClick={() => singleLocationPlan && handleCheckout(singleLocationPlan.id, 1, (singleLocationPlan.unit_amount / 100).toFixed(2), "single")}
                >
                  Subscribe
                </Button>
              )}
            </div>

            {/* Multi-Location Plan */}
            <div
              onClick={() => setSelectedPlan("multi")}
              className={`cursor-pointer border-2 rounded-2xl p-5 relative transition-all bg-white text-start ${selectedPlan === "multi"
                  ? "border-[#881337] bg-rose-50/50 shadow-xl"
                  : "border-slate-200 hover:border-slate-300"
                }`}
            >
              <div className="absolute top-0 right-0 bg-[#881337] text-white text-[12px] font-bold px-4 py-1.5 rounded-tr-[14px] rounded-bl-xl uppercase tracking-wider">
                MOST POPULAR
              </div>

              <h3 className="text-md font-bold text-slate-900 mb-1 text-start">
                Multi-Location
              </h3>
              <div className="flex items-baseline gap-1 mb-1 text-start">
                <span className="text-2xl font-bold text-[#881337]">
                  ${calculateMultiLocationPrice(multiLocationCount).toFixed(2)}
                </span>
                <span className=" font-medium text-xs">
                  + $50 / additional location
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-4 text-start">
                For restaurant groups and small chains that need consistency and
                brand excellence
              </p>

              <ul className="space-y-2 mb-2 text-sm">
                {[
                  "Everything in Single Location",
                  "Centralized analytics for multiple restaurants",
                  "Shared menu libraries and standardized lessons",
                  "Director-level dashboards for regional performance",
                  "Consistent guest experiences across all locations",
                ].map((feature, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-slate-700">
                    <Check
                      size={18}
                      className="text-white bg-[#881337] rounded-full m-2 shrink-0 mt-0.5"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {currentPlan && currentPlan.toLowerCase().includes("multi") ? (
                <div>
                  <div className="text-sm text-gray-700 mb-2">Up to {locationQuantity} locations included</div>
                  <Button className="w-full py-3 bg-gray-300 text-gray-700 rounded-xl text-sm" disabled>
                    Already subscribed
                  </Button>
                </div>
              ) : (
                <div>
                  <Button
                    className="w-full py-3 bg-[#881337] text-white  rounded-xl hover:bg-[#6e0f2c] transition-colors shadow-lg shadow-red-900/20 text-sm"
                    onClick={() => setMultiLocationModalOpen(true)}
                  >
                    Subscribe
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-500 text-xs mb-1 text-start">
              Need enterprise-level features or white-glove onboarding?
            </p>
            <button className="text-[#881337] text-sm block font-bold hover:underline text-start">
              Contact us for a custom plan
            </button>
          </div>
        </div>
      </div>

      {/* Multi-Location Modal */}
      <Modal isOpen={multiLocationModalOpen} onClose={() => setMultiLocationModalOpen(false)}>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Select Number of Locations</h2>
          <input
            type="number"
            min={2}
            max={50}
            value={multiLocationCount}
            onChange={(e) => setMultiLocationCount(Number(e.target.value))}
            className="border rounded px-2 py-1 w-24 text-lg font-bold mb-4"
            style={{ appearance: "textfield" }}
          />
          <div className="mb-4">
            Total: <span className="font-bold">${calculateMultiLocationPrice(multiLocationCount).toFixed(2)}/month</span>
          </div>
          <div className="flex gap-2">
            <Button
              className="bg-[#881337] text-white flex-1"
              onClick={async () => {
                setMultiLocationModalOpen(false);
                if (multiLocationPlan) {
                  const amountDollars = calculateMultiLocationPrice(multiLocationCount).toFixed(2);
                  await handleCheckout(multiLocationPlan?.product?.id || multiLocationPlan?.id, multiLocationCount, amountDollars, "multi");
                } else {
                  setSnackbarMessage("Plan data not loaded yet.");
                  setSnackbarSeverity("error");
                  setSnackbarOpen(true);
                }
              }}
            >
              Confirm
            </Button>
            <Button className="bg-gray-300 flex-1" onClick={() => setMultiLocationModalOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Plans;
